import { useFetch } from 'hooks/use-fetch/use-fetch';
import { ResponseModel, User } from 'common/interfaces';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PAGES, ReactQueryKeys } from 'common/enums';
import { accountRequestConfig } from 'request-config/account/account.request-config';
import { UserInfoResponse } from './use-user-info.types';
import { AxiosResponse } from 'axios';
import { setIsExportsFlowExist, setRoleSwitched, setUser } from 'actions/app_action';
import { defaultGuid } from '../../../constants';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSessionState } from '../../use-session-state';
import isEqual from 'lodash/isEqual';

const useUserInfo = (enabled = true) => {
  const dispatch = useDispatch();
  const user = useTypedSelector<User>(state => state['app'].user);
  const roleSwitched = useTypedSelector<User>(state => state['app'].roleSwitched);
  const { state: rolesCached, setSessionState: setRolesCached } = useSessionState<UserInfoResponse | null>(
    'roles',
    null
  );

  const history = useHistory();

  const { fetch } = useFetch<ResponseModel<UserInfoResponse>>({
    loadingKey: 'useUserInfo',
    isShowErrorSnackbar: false,
    ...accountRequestConfig.getUserInfo()
  });

  const modifyResponse = (data: AxiosResponse<UserInfoResponse>) => {
    if (data?.status === 200) {
      if (!isEqual(rolesCached, data.data)) {
        setRolesCached(data.data);
      }

      return data.data;
    }

    return undefined;
  };

  const { isFetched, isSuccess, refetch } = useQuery<
    AxiosResponse<UserInfoResponse>,
    unknown,
    UserInfoResponse | undefined
  >({
    queryKey: [ReactQueryKeys.GetUserInfo],
    queryFn: fetch,
    select: modifyResponse,
    enabled: enabled && !rolesCached
  });

  const queryClient = useQueryClient();

  const clearCacheExceptOne = (queryKeyToKeep: string[]) => {
    const allQueries = queryClient.getQueryCache().findAll();

    allQueries.forEach(query => {
      if (JSON.stringify(query.queryKey) !== JSON.stringify(queryKeyToKeep)) {
        queryClient.cancelQueries({ queryKey: query.queryKey });
        queryClient.removeQueries({ queryKey: query.queryKey });
      }
    });
  };

  useEffect(() => {
    if (isFetched && !rolesCached?.mainB2CWebrolleId) {
      dispatch(
        setUser({
          ...user,
          companyId: defaultGuid
        })
      );
    }
  }, [rolesCached?.mainB2CWebrolleId, isFetched]);

  useEffect(() => {
    if (rolesCached) {
      dispatch(setUser({ ...user, ...getUserData(rolesCached) }));
      dispatch(
        setIsExportsFlowExist(rolesCached?.appPages.pages.some(pageItem => pageItem.url === PAGES.EXPORTS) || false)
      );
      window.clarity && window.clarity('set', 'email', rolesCached.email);

      if (roleSwitched) {
        dispatch(setRoleSwitched(false));
        history.push(PAGES.TASKS);
        setTimeout(() => clearCacheExceptOne([ReactQueryKeys.GetUserInfo]), 0);
      }
    }
  }, [rolesCached?.mainB2CWebrolleId]);

  return { userInfo: rolesCached, isFetched, isSuccess, refetch };
};

export default useUserInfo;

const getUserData = (userResponse: UserInfoResponse) => {
  const userRoles = userResponse.roles.map(role => ({ id: role.uds_b2c_webroleid, name: role.uds_name }));
  return {
    ...userResponse,
    id: userResponse.id,
    name: userResponse.fullName,
    email: userResponse.email,
    companyId: userResponse.account?.id,
    isTestUser: userResponse.isTestUser,
    companyName: userResponse.account?.name,
    roles: userRoles,
    mainRole: userResponse.mainB2CWebrolleId
      ? userRoles.find(role => role.id === userResponse.mainB2CWebrolleId) || userRoles[0]
      : userRoles[0],
    childCompanies: userResponse.childCompanies,
    appPages: userResponse.appPages.pages || [],
    customPages: userResponse.customPages || [],
    driversCheckFunction: {
      isManager: userResponse.isManager,
      isController: userResponse.isController
    },
    schadenmeldenLink: userResponse.schadenmeldenLink
  };
};
